import React, {ReactNode} from 'react'
import {TableRowName} from '../../../styledComponents/adminPage/Table'
import PPRadioGroup from '../../shared/forms/PPRadioGroup'
import {
    AVAILABLE_OR_NOT,
    DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL,
    HOURS,
    RESERVATION_TIME_UNIT_MINUTES,
} from '../../../CodeMaster'
import {Nullable} from '../../../app/TypeHelpers'
import {ReservationSettings} from './ReservationSettingsComponent'
import PPNumericField from '../../shared/forms/PPNumericField'
import {HKConstants} from '../../../HKConstants'
import {Messages} from '../../../Messages'
import PPSelect from '../../shared/forms/PPSelect'
import {LeftColumnFlexContainer, LeftRowFlexContainer} from '../../../styledComponents/FlexComponents'
import { Box, TableRow, TableCell, InputAdornment } from '@mui/material';
import {StyledLabel} from '../../../styledComponents/StyledTypeDefinitions'
import styled from 'styled-components'
import {numberOrNull, stringOrEmpty} from '../../../stringHelper/stringHelpers'
import {addEmptyData} from '../../../componentHelper/selectHelper'
import equal from 'fast-deep-equal'
import PPErrorLabel from '../../shared/forms/PPErrorLabel'
import EventEmitterExAdmin from '../../shared/pageBaseComponents/EventEmitterExAdmin'
import {isIE} from 'react-device-detect'
import {SelectChangeEvent} from '@mui/material/Select'

interface ReservationCancelSettings {
    cancelAvailability: AVAILABLE_OR_NOT
    unitOfCancelReceptionLimit: DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL
    cancelReceptionLimitDate: Nullable<number>
    cancelReceptionLimitTimeHours: Nullable<number>
    cancelReceptionLimitTimeMinutes: Nullable<number>
}

interface Props extends ReservationCancelSettings {
    eventEmitter: EventEmitterExAdmin
    handleReservationSettingsChange: (reservationSettings: Partial<ReservationSettings>) => void
}

class ReservationCancelSettingsComponent extends React.Component<Props> {

    static displayName: string = 'ReservationCancelSettingsComponent'

    constructor(props: Readonly<Props>) {
        super(props)

        this.handleCancelAvailabilityChange = this.handleCancelAvailabilityChange.bind(this)
        this.handleUnitOfCancelReceptionLimitChange = this.handleUnitOfCancelReceptionLimitChange.bind(this)
    }

    shouldComponentUpdate(nextProps: Readonly<Props>): boolean {
        return !(this.props === nextProps || equal(this.props, nextProps))
    }

    handleCancelAvailabilityChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const {handleReservationSettingsChange, eventEmitter, ...rest} = this.props
        const cancelAvailability: AVAILABLE_OR_NOT =
            AVAILABLE_OR_NOT.codeToEnumConstants(`${event.target.value}`)

        handleReservationSettingsChange({
            ...rest,
            cancelAvailability: cancelAvailability,
        })
    }

    handleUnitOfCancelReceptionLimitChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const {handleReservationSettingsChange, eventEmitter, ...rest} = this.props
        const unitOfCancelReceptionLimit: DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL =
            DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.codeToEnumConstants(`${event.target.value}`)

        handleReservationSettingsChange({
            ...rest,
            unitOfCancelReceptionLimit: unitOfCancelReceptionLimit,
        })
    }

    handleNumericValueChange = (target: string) =>
        (event: React.ChangeEvent<{ value: unknown }> | SelectChangeEvent<unknown>) => {
        const {handleReservationSettingsChange, eventEmitter, ...rest} = this.props

        handleReservationSettingsChange({
            ...rest,
            [target]: numberOrNull(`${event.target.value}`),
        })
    }

    renderSpecifyDay(): ReactNode {
        if (this.props.unitOfCancelReceptionLimit !==
            DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.SPECIFIED_DAY) {
            return null
        }
        return (<>
            <Box marginRight={'15px'}/>
            <CancelLabel>
                （当日除く）
            </CancelLabel>
            <label>
                <CancelReceptionLimitDate
                    InputProps={{
                        inputProps: {
                            min: 1,
                            max: HKConstants.VMN.FAC.MAX_CANCELLATION_DEADLINE_DAY,
                        },
                        endAdornment: <LimitInputAdornment position="end">日前まで</LimitInputAdornment>,
                    }}
                    value={stringOrEmpty(this.props.cancelReceptionLimitDate)}
                    onChange={this.handleNumericValueChange('cancelReceptionLimitDate')}
                    hint={Messages.HFACA0003.toString()}
                />
            </label>
        </>)
    }

    renderSpecifyTime(): ReactNode {
        if (this.props.unitOfCancelReceptionLimit !==
            DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.SPECIFIED_TIME) {
            return null
        }
        return (<>
            <Box marginLeft={'25px'} marginRight={'auto'}>
                <LeftColumnFlexContainer>
                    <LeftRowFlexContainer>
                        <CancelLabel>
                            当日
                        </CancelLabel>
                        <label>
                            <LeftRowFlexContainer>
                                <PPSelect
                                    data={addEmptyData(HOURS.values())}
                                    value={stringOrEmpty(this.props.cancelReceptionLimitTimeHours)}
                                    onChange={this.handleNumericValueChange('cancelReceptionLimitTimeHours')}
                                />
                                時間
                            </LeftRowFlexContainer>
                        </label>
                        <label>
                            <LeftRowFlexContainer>
                                <PPSelect
                                    data={addEmptyData(RESERVATION_TIME_UNIT_MINUTES.values())}
                                    value={stringOrEmpty(this.props.cancelReceptionLimitTimeMinutes)}
                                    onChange={this.handleNumericValueChange('cancelReceptionLimitTimeMinutes')}
                                />
                                分前まで
                            </LeftRowFlexContainer>
                        </label>
                    </LeftRowFlexContainer>
                    <PPErrorLabel
                        eventEmitter={this.props.eventEmitter}
                        eventKeys={['reservationSetting.cancelReceptionLimitTime']}
                    />
                </LeftColumnFlexContainer>
            </Box>
        </>)
    }

    renderCancelReceptionLimit(): ReactNode {
        if (this.props.cancelAvailability === AVAILABLE_OR_NOT.UNAVAILABLE) {
            return null
        }
        return (<>
            <TableRow>
                <TableRowName>キャンセル可能期間</TableRowName>
                <TableCell>
                    <LeftRowFlexContainer>
                        <PPRadioGroup
                            row
                            data={DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.values()}
                            value={this.props.unitOfCancelReceptionLimit.value}
                            onChange={this.handleUnitOfCancelReceptionLimitChange}
                        />
                        {this.renderSpecifyDay()}
                        {this.renderSpecifyTime()}
                    </LeftRowFlexContainer>
                </TableCell>
            </TableRow>
        </>)
    }

    render(): ReactNode {
        return (<>
            <TableRow>
                <TableRowName>キャンセル設定</TableRowName>
                <TableCell>
                    <PPRadioGroup
                        row
                        data={AVAILABLE_OR_NOT.values()}
                        value={this.props.cancelAvailability.value}
                        onChange={this.handleCancelAvailabilityChange}
                    />
                </TableCell>
            </TableRow>
            {this.renderCancelReceptionLimit()}
        </>)
    }
}

const CancelLabel: StyledLabel = styled.label`
    margin-right: 15px;
`

const CancelReceptionLimitDate = styled(PPNumericField)`
  width: 125px;
`

const LimitInputAdornment = styled(InputAdornment)`
  ${isIE && `
    margin-left: -40px !important;
  `}
`

export default ReservationCancelSettingsComponent
